/**
 * components/procurement/AIPromptDialog.jsx — natural-language → scenario.
 *
 * The user describes the scenario in plain English; the LLM returns structured
 * assumptions (metered at 1 credit/generation). The user reviews the proposed
 * changes, names the scenario, and creates it.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Sparkles, Loader2, Wand2 } from 'lucide-react';

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { BeaconShimmer } from './BeaconShimmer';
import { aiGenerateScenario, createScenario, getAiCredits } from '../../api/budgets';

const EXAMPLES = [
  'Add 6% annual supply escalation and a 3% management fee on total',
  'Set electricity supply to 0.085 $/kWh for the first year, then escalate 4%/yr',
  'Lock in contracted gas rates and add 8% tax',
];

export function AIPromptDialog({ open, onOpenChange, accountId, budgetId, onSaved }) {
  const [prompt, setPrompt] = useState('');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [credits, setCredits] = useState(null);

  useEffect(() => {
    if (!open) { setPrompt(''); setResult(null); setName(''); return; }
    getAiCredits({ accountId }).then(setCredits).catch(() => {});
  }, [open, accountId]);

  const generate = async () => {
    if (prompt.trim().length < 3) { toast.error('Describe the scenario first'); return; }
    setGenerating(true);
    setResult(null);
    try {
      const res = await aiGenerateScenario({ accountId, id: budgetId, prompt: prompt.trim() });
      setResult(res);
      setName(res.name_suggestion || 'AI scenario');
      setCredits((c) => (c ? { ...c, balance: res.ai.balance, consumed: (c.consumed || 0) + 1 } : c));
      toast.success('Scenario drafted', { description: `${res.ai.credits_charged} credit used · ${res.ai.balance} left` });
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      toast.error('AI generation failed', { description: msg });
    } finally {
      setGenerating(false);
    }
  };

  const createIt = async () => {
    if (!result) return;
    if (!name.trim()) { toast.error('Name the scenario'); return; }
    setCreating(true);
    try {
      await createScenario({ accountId, id: budgetId, payload: { name: name.trim(), assumptions: result.assumptions } });
      toast.success('Scenario created');
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error('Could not create scenario', { description: e?.response?.data?.detail || e.message });
    } finally {
      setCreating(false);
    }
  };

  const a = result?.assumptions;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="ai-prompt-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-violet-600" /> Model with AI
          </DialogTitle>
          <DialogDescription>
            Describe the scenario in plain English. The AI drafts the rates, escalation, tax and
            charges — you review and create it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="ai-prompt">Your prompt</Label>
              {credits && (
                <span className="text-xs text-muted-foreground" data-testid="ai-credits-label">
                  {credits.balance} credits left
                </span>
              )}
            </div>
            <Textarea id="ai-prompt" rows={3} value={prompt} onChange={(e) => setPrompt(e.target.value)}
              placeholder="e.g. Add 6% supply escalation and a 3% management fee on total"
              data-testid="ai-prompt-input" />
            <div className="flex flex-wrap gap-1.5 pt-1">
              {EXAMPLES.map((ex) => (
                <button key={ex} onClick={() => setPrompt(ex)} type="button"
                  className="text-[11px] px-2 py-1 rounded-full border bg-muted/40 hover:bg-muted text-muted-foreground"
                  data-testid="ai-example-chip">
                  {ex}
                </button>
              ))}
            </div>
          </div>

          <Button onClick={generate} disabled={generating} className="w-full" data-testid="ai-generate-btn">
            {generating ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Drafting…</> : <><Wand2 className="w-4 h-4 mr-1.5" /> Generate (1 credit)</>}
          </Button>

          {generating && (
            <div className="border rounded-md p-3 bg-violet-50/50 flex justify-center">
              <BeaconShimmer />
            </div>
          )}

          {result && (
            <div className="border rounded-md p-3 bg-violet-50/50 space-y-3" data-testid="ai-result">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Proposed changes</div>
                <p className="text-sm">{result.summary}</p>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {a?.escalation?.supply != null && <Badge variant="secondary">Supply esc {a.escalation.supply}%</Badge>}
                {a?.escalation?.delivery != null && <Badge variant="secondary">Delivery esc {a.escalation.delivery}%</Badge>}
                {a?.tax_pct != null && <Badge variant="secondary">Tax {a.tax_pct}%</Badge>}
                {(a?.rate_overrides || []).length > 0 && <Badge variant="secondary">{a.rate_overrides.length} rate override(s)</Badge>}
                {(a?.added_charges || []).map((c, i) => (
                  <Badge key={i} variant="secondary">{c.label} {c.value}{c.kind === 'pct' ? '%' : '$'}</Badge>
                ))}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ai-name">Scenario name</Label>
                <Input id="ai-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="ai-scenario-name" />
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          {result && (
            <Button onClick={createIt} disabled={creating} data-testid="ai-create-scenario-btn">
              {creating ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Creating…</> : 'Create Scenario'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
